import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useTypedParams } from 'react-router-typesafe-routes';
import { useClickAnyWhere } from 'usehooks-ts';
import MoreIcon from '@/assets/images/icons/more.svg?react';
import useDeleteTags from '@/hooks/tags/use-delete-tags';
import useUpdateTag from '@/hooks/tags/use-update-tag';
import ROUTES from '@/routes';
import { makePrompt } from '@/stores/prompter';
import cn from '@/utils/merge-class-names';

interface Props {
  className?: string;
}

export default function TagHeaderMenu({ className }: Props) {
  const { tagId } = useTypedParams(ROUTES.CONTACT.TAGS.BY_ID);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const detailRef = useRef<HTMLDetailsElement>(null);
  const { mutateAsyncWithPrompt: promptUpdateTag } = useUpdateTag(tagId);
  const { mutateAsync: deleteTag } = useDeleteTags();

  const closeMenu = () => detailRef.current?.hasAttribute('open') && detailRef.current?.removeAttribute('open');

  useClickAnyWhere(closeMenu);

  const handleDeleteTag = () =>
    makePrompt({
      key: 'delete-tag',
      type: 'dialog',
      title: t('contact.tags.delete.delete-tag'),
      content: t('contact.tags.delete.confirm-message'),
      promise: async () => {
        const res = await deleteTag([tagId]);
        if (res.Code === 0) {
          navigate(-1);
        }
      },
    });

  return (
    <details
      className={cn('dropdown dropdown-end', className)}
      ref={detailRef}>
      <summary
        tabIndex={0}
        role='button'
        className='btn btn-square mode-embedded:btn-sm btn-ghost text-inherit'>
        <MoreIcon className='size-4 rotate-90' />
      </summary>

      <div className='menu dropdown-content z-50 rounded-lg min-w-22 bg-neutral-0 shadow-lg'>
        <button
          type='button'
          className='whitespace-nowrap btn btn-ghost text-neutral-content'
          onClick={() => promptUpdateTag()}>
          {t('contact.tags.change-name')}
        </button>

        <button
          type='button'
          className='whitespace-nowrap btn btn-ghost text-error hover:bg-error/10'
          onClick={handleDeleteTag}>
          {t('contact.tags.delete.delete-tag')}
        </button>
      </div>
    </details>
  );
}
